import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import PrintableDocument from '@/components/PrintableDocument';
import CorrespondenceTab from '@/components/correspondence/CorrespondenceTab';
import { api } from '@/api';
import { toast } from 'sonner';
import usePermissions from '@/hooks/usePermissions';
import { 
  ArrowLeft, FileImage, Download, Plus, Eye, RefreshCw, Upload,
  AlertTriangle, CheckCircle, Clock, Calendar, Building, User,
  MessageSquare, FileText, Send, ExternalLink, History, Layers,
  AlertCircle, Sparkles, ChevronRight, Link2, Pencil
} from 'lucide-react';

const DISCIPLINE_COLORS = {
  'Architectural': 'bg-blue-100 text-blue-700',
  'Structural': 'bg-orange-100 text-orange-700',
  'Civil': 'bg-emerald-100 text-emerald-700',
  'Electrical': 'bg-yellow-100 text-yellow-700',
  'Mechanical': 'bg-purple-100 text-purple-700',
  'General': 'bg-gray-100 text-gray-700',
};

const STATUS_COLORS = {
  'Active': 'bg-emerald-100 text-emerald-700',
  'Superseded': 'bg-amber-100 text-amber-700',
  'Cancelled': 'bg-red-100 text-red-700',
};

const IMPACT_COLORS = {
  'Yes': 'bg-red-100 text-red-700',
  'No': 'bg-emerald-100 text-emerald-700',
  'Unknown': 'bg-slate-100 text-slate-600',
};

export default function DrawingDetail() {
  const { projectId, drawingId } = useParams();
  const navigate = useNavigate();
  const permissions = usePermissions();
  
  const [drawing, setDrawing] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRevision, setSelectedRevision] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [consultants, setConsultants] = useState([]);
  const [internalTeam, setInternalTeam] = useState([]);
  
  // Dialogs
  const [showAddRevision, setShowAddRevision] = useState(false);
  const [showCreateRfi, setShowCreateRfi] = useState(false);
  const [showConvertEvent, setShowConvertEvent] = useState(false);
  const [showDistribute, setShowDistribute] = useState(false);
  const [showAddQuery, setShowAddQuery] = useState(false);
  
  // Forms
  const [revisionForm, setRevisionForm] = useState({
    revision_code: '',
    issue_purpose: 'Information',
    revision_notes: '',
    issued_by_consultant_id: '',
    issue_date: '',
    received_date: new Date().toISOString().split('T')[0],
    run_ai_comparison: true
  });
  const [revisionFile, setRevisionFile] = useState(null);
  const [uploadingRevision, setUploadingRevision] = useState(false);
  const [extractingMetadata, setExtractingMetadata] = useState(false);
  const [rfiForm, setRfiForm] = useState({
    title: '',
    query: '',
    priority: 'Medium',
    required_response_date: ''
  });
  const [eventForm, setEventForm] = useState({
    event_type: '',
    narrative: '',
    responsible_party: 'Consultant',
    start_date: ''
  });
  const [queryForm, setQueryForm] = useState({
    query_type: 'Internal',
    question_text: ''
  });
  const [selectedRecipients, setSelectedRecipients] = useState([]);

  useEffect(() => {
    loadDrawing();
    loadTeams();
  }, [projectId, drawingId]);

  const loadDrawing = async () => {
    setLoading(true);
    try {
      const res = await api.drawings.get(projectId, drawingId);
      setDrawing(res.data);
      
      // Select current revision by default
      if (res.data.revisions?.length > 0) {
        setSelectedRevision(res.data.revisions[0]);
      }
      
      // Load project info for print header
      try {
        const projectRes = await api.projects.getById(projectId);
        setProject(projectRes.data.project || projectRes.data);
      } catch (e) {
        console.error('Failed to load project:', e);
      }
    } catch (error) {
      toast.error('Failed to load drawing');
      navigate(`/projects/${projectId}`);
    } finally {
      setLoading(false);
    }
  };

  const loadTeams = async () => {
    try {
      const [profRes, intRes] = await Promise.all([
        api.teams.getProfessional(projectId).catch(() => ({ data: [] })),
        api.teams.getInternal(projectId).catch(() => ({ data: [] }))
      ]);
      setConsultants(profRes.data || []);
      setInternalTeam(intRes.data || []);
    } catch (error) {
      console.error('Failed to load teams:', error);
    }
  };

  const handleRevisionFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are allowed');
      return;
    }
    
    setRevisionFile(file);
    setExtractingMetadata(true);
    
    try {
      // Upload and extract metadata
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.drawings.uploadPdf(projectId, formData, true);
      
      if (res.data.extraction) {
        const ext = res.data.extraction;
        // Pre-fill form with extracted data
        setRevisionForm(prev => ({
          ...prev,
          revision_code: ext.revision_code || prev.revision_code,
          issue_purpose: ext.issue_purpose || prev.issue_purpose,
          issue_date: ext.issue_date || prev.issue_date,
          pdf_file_url: res.data.file_url,
          pdf_file_name: res.data.file_name,
          pdf_file_size: res.data.file_size
        }));
        
        // Auto-match consultant
        if (ext.consultant_name) {
          const match = consultants.find(c => 
            c.company_name?.toLowerCase().includes(ext.consultant_name.toLowerCase())
          );
          if (match) {
            setRevisionForm(prev => ({ ...prev, issued_by_consultant_id: match.id }));
          }
        }
        
        toast.success('Metadata extracted from PDF');
      } else {
        setRevisionForm(prev => ({
          ...prev,
          pdf_file_url: res.data.file_url,
          pdf_file_name: res.data.file_name,
          pdf_file_size: res.data.file_size
        }));
      }
    } catch (error) {
      toast.error('Failed to upload PDF');
      console.error(error);
    } finally {
      setExtractingMetadata(false);
    }
  };

  const handleAddRevision = async () => {
    if (!revisionForm.revision_code || !revisionForm.issued_by_consultant_id) {
      toast.error('Revision code and consultant are required');
      return;
    }
    
    if (!revisionForm.pdf_file_url) {
      toast.error('Please upload a PDF file');
      return;
    }
    
    try {
      setUploadingRevision(true);
      const result = await api.drawingRevisions.create(projectId, drawingId, {
        ...revisionForm,
        pdf_file_url: revisionForm.pdf_file_url,
        pdf_file_name: revisionForm.pdf_file_name,
        run_ai_comparison: revisionForm.run_ai_comparison
      });
      
      if (result.data.ai_comparison) {
        toast.success('Revision added with AI analysis');
      } else {
        toast.success('Revision added');
      }
      
      setShowAddRevision(false);
      setRevisionFile(null);
      setRevisionForm({
        revision_code: '',
        issue_purpose: 'Information',
        revision_notes: '',
        issued_by_consultant_id: '',
        issue_date: '',
        received_date: new Date().toISOString().split('T')[0],
        run_ai_comparison: true
      });
      loadDrawing();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add revision');
    } finally {
      setUploadingRevision(false);
    }
  };

  const handleCreateRfi = async () => {
    if (!rfiForm.title || !rfiForm.query) {
      toast.error('Title and query are required');
      return;
    }
    
    try {
      const res = await api.drawingRevisions.createRfi(selectedRevision.id, rfiForm);
      toast.success(`RFI ${res.data.rfi_number} created`);
      setShowCreateRfi(false);
      loadDrawing();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create RFI');
    }
  };

  const handleConvertToEvent = async () => {
    if (!eventForm.event_type) {
      toast.error('Event type is required');
      return;
    }
    
    try {
      const res = await api.drawingRevisions.convertToDelayEvent(selectedRevision.id, eventForm);
      toast.success('Delay Event created');
      setShowConvertEvent(false);
      loadDrawing();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    }
  };

  const handleDistribute = async () => {
    if (selectedRecipients.length === 0) {
      toast.error('Select at least one recipient');
      return;
    }
    
    try {
      await api.drawingRevisions.distribute(selectedRevision.id, selectedRecipients);
      toast.success('Drawing distributed');
      setShowDistribute(false);
      loadDrawing();
    } catch (error) {
      toast.error('Failed to distribute');
    }
  };

  const handleAcknowledge = async () => {
    try {
      await api.drawingRevisions.acknowledge(selectedRevision.id);
      toast.success('Drawing acknowledged');
      loadDrawing();
    } catch (error) {
      toast.error('Failed to acknowledge');
    }
  };

  const handleAddQuery = async () => {
    if (!queryForm.question_text) {
      toast.error('Question is required');
      return;
    }
    
    try {
      await api.drawingRevisions.addQuery(selectedRevision.id, queryForm);
      toast.success('Query added');
      setShowAddQuery(false);
      setQueryForm({ query_type: 'Internal', question_text: '' });
      loadDrawing();
    } catch (error) {
      toast.error('Failed to add query');
    }
  };

  const handleGenerateDiff = async () => {
    try {
      toast.info('Generating diff summary...');
      const res = await api.drawingRevisions.generateDiff(selectedRevision.id);
      if (res.data.diff_summary) {
        toast.success('Diff summary generated');
        loadDrawing();
      } else {
        toast.info(res.data.message || 'No previous revision to compare');
      }
    } catch (error) {
      toast.error('Failed to generate diff');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-ZA', { 
      day: '2-digit', month: 'short', year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </DashboardLayout>
    );
  }

  if (!drawing) {
    return (
      <DashboardLayout>
        <div className="p-8 text-center">
          <FileImage className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Drawing not found</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="drawing-detail-page">
        {/* Header */}
        <div className="mb-6">
          <button 
            onClick={() => navigate(`/projects/${projectId}`)}
            className="flex items-center text-slate-500 hover:text-slate-700 text-sm mb-3"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Project
          </button>
          
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-barlow font-bold uppercase">{drawing.drawing_number}</h1>
                <Badge className={`${STATUS_COLORS[drawing.status]} rounded-sm`}>
                  {drawing.status}
                </Badge>
                <Badge className={`${DISCIPLINE_COLORS[drawing.discipline] || DISCIPLINE_COLORS.General} rounded-sm`}>
                  {drawing.discipline}
                </Badge>
              </div>
              <p className="text-slate-600">{drawing.drawing_title || 'Untitled Drawing'}</p>
            </div>
            
            <div className="flex gap-2">
              {selectedRevision?.pdf_file_url && (
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-sm"
                  onClick={() => window.open(selectedRevision.pdf_file_url, '_blank')}
                >
                  <Download className="h-4 w-4 mr-1" />
                  Download PDF
                </Button>
              )}
              <Button
                size="sm"
                className="rounded-sm bg-slate-900"
                onClick={() => setShowAddRevision(true)}
                data-testid="add-revision-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Revision
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left: Revision List */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-slate-200 rounded-sm shadow-sm">
              <div className="p-3 border-b border-slate-100">
                <h3 className="text-sm font-barlow font-bold uppercase text-slate-700 flex items-center gap-2">
                  <History className="h-4 w-4" />
                  Revisions ({drawing.revisions?.length || 0})
                </h3>
              </div>
              <div className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
                {(drawing.revisions || []).map((rev, idx) => (
                  <div
                    key={rev.id}
                    className={`p-3 cursor-pointer transition-colors ${
                      selectedRevision?.id === rev.id 
                        ? 'bg-blue-50 border-l-2 border-blue-500' 
                        : 'hover:bg-slate-50'
                    }`}
                    onClick={() => setSelectedRevision(rev)}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="font-mono font-bold text-slate-900">Rev {rev.revision_code}</span>
                        {idx === 0 && (
                          <Badge className="ml-2 bg-emerald-100 text-emerald-700 rounded-sm text-xs">
                            Current
                          </Badge>
                        )}
                      </div>
                      <Badge className="bg-slate-100 text-slate-600 rounded-sm text-xs">
                        {rev.issue_purpose}
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {rev.issued_by_consultant_name || 'Unknown'}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {formatDate(rev.issue_date)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Revision Details */}
          <div className="lg:col-span-3 space-y-6">
            {selectedRevision && (
              <>
                {/* Revision Info Card */}
                <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-5">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-sm font-barlow font-bold uppercase text-slate-700">
                      Revision {selectedRevision.revision_code} Details
                    </h3>
                    <div className="flex gap-2">
                      {selectedRevision.distribution_status === 'Distributed' && 
                       !selectedRevision.acknowledged_at && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-sm border-emerald-300 text-emerald-700"
                          onClick={handleAcknowledge}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Acknowledge
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-xs text-slate-500 uppercase font-bold">Issued By</p>
                      <p className="text-slate-900">{selectedRevision.issued_by_consultant_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase font-bold">Issue Date</p>
                      <p className="text-slate-900">{formatDate(selectedRevision.issue_date)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase font-bold">Received Date</p>
                      <p className="text-slate-900">{formatDate(selectedRevision.received_date)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase font-bold">Purpose</p>
                      <p className="text-slate-900">{selectedRevision.issue_purpose}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase font-bold">Sheets</p>
                      <p className="text-slate-900">{selectedRevision.sheet_count || 1}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase font-bold">Distribution</p>
                      <Badge className={`${
                        selectedRevision.distribution_status === 'Acknowledged' 
                          ? 'bg-emerald-100 text-emerald-700'
                          : selectedRevision.distribution_status === 'Distributed'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-slate-100 text-slate-600'
                      } rounded-sm text-xs`}>
                        {selectedRevision.distribution_status}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase font-bold">AI Confidence</p>
                      <p className="text-slate-900">{selectedRevision.ai_extraction_confidence || 0}%</p>
                    </div>
                  </div>

                  {/* Impact Classification */}
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <p className="text-xs text-slate-500 uppercase font-bold mb-2">Impact Classification</p>
                    <div className="flex gap-3">
                      <Badge className={`${IMPACT_COLORS[selectedRevision.potential_time_impact]} rounded-sm text-xs`}>
                        Time: {selectedRevision.potential_time_impact}
                      </Badge>
                      <Badge className={`${IMPACT_COLORS[selectedRevision.potential_cost_impact]} rounded-sm text-xs`}>
                        Cost: {selectedRevision.potential_cost_impact}
                      </Badge>
                      <Badge className={`${IMPACT_COLORS[selectedRevision.scope_change_suspected]} rounded-sm text-xs`}>
                        Scope: {selectedRevision.scope_change_suspected}
                      </Badge>
                      <Badge className="bg-slate-100 text-slate-600 rounded-sm text-xs">
                        Urgency: {selectedRevision.urgency}
                      </Badge>
                    </div>
                  </div>
                  
                  {/* Revision Notes */}
                  {selectedRevision.revision_notes && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <p className="text-xs text-slate-500 uppercase font-bold mb-2">Revision Notes</p>
                      <p className="text-slate-700 text-sm bg-slate-50 p-3 rounded-sm">{selectedRevision.revision_notes}</p>
                    </div>
                  )}
                  
                  {/* AI Change Summary */}
                  {(selectedRevision.ai_change_summary || selectedRevision.ai_detected_clouds?.length > 0) && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="h-4 w-4 text-purple-500" />
                        <p className="text-xs text-slate-500 uppercase font-bold">AI Analysis</p>
                      </div>
                      {selectedRevision.ai_change_summary && (
                        <p className="text-slate-700 text-sm bg-purple-50 p-3 rounded-sm mb-2">{selectedRevision.ai_change_summary}</p>
                      )}
                      {selectedRevision.ai_detected_clouds?.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-slate-500 font-bold mb-1">Detected Revision Clouds:</p>
                          <ul className="list-disc list-inside text-sm text-slate-600">
                            {selectedRevision.ai_detected_clouds.map((cloud, i) => (
                              <li key={i}>{cloud}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-sm"
                    onClick={() => setShowDistribute(true)}
                  >
                    <Send className="h-4 w-4 mr-1" />
                    Distribute
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-sm border-cyan-300 text-cyan-700"
                    onClick={() => setShowCreateRfi(true)}
                    data-testid="create-rfi-btn"
                  >
                    <FileText className="h-4 w-4 mr-1" />
                    Create RFI
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-sm border-orange-300 text-orange-700"
                    onClick={() => {
                      setEventForm({
                        event_type: `Late / Revised Drawing - ${drawing.drawing_number}`,
                        narrative: '',
                        responsible_party: 'Consultant',
                        start_date: selectedRevision.received_date || selectedRevision.issue_date || ''
                      });
                      setShowConvertEvent(true);
                    }}
                    data-testid="convert-event-btn"
                  >
                    <AlertTriangle className="h-4 w-4 mr-1" />
                    Convert to Delay Event
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-sm"
                    onClick={() => setShowAddQuery(true)}
                  >
                    <MessageSquare className="h-4 w-4 mr-1" />
                    Add Query
                  </Button>
                  {selectedRevision.supersedes_revision_id && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-sm"
                      onClick={handleGenerateDiff}
                    >
                      <Sparkles className="h-4 w-4 mr-1" />
                      Generate Diff
                    </Button>
                  )}
                </div>

                {/* Tabs for Change Log, Queries, Correspondence, Links */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  {/* Mobile: Dropdown */}
                  <div className="sm:hidden mb-4">
                    <Select value={activeTab} onValueChange={setActiveTab}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select view" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="details">Change Log</SelectItem>
                        <SelectItem value="queries">Queries ({selectedRevision.queries?.length || 0})</SelectItem>
                        <SelectItem value="correspondence">Correspondence</SelectItem>
                        <SelectItem value="links">Linked Items</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Desktop: Tab List */}
                  <TabsList className="bg-slate-100 p-1 rounded-sm hidden sm:inline-flex">
                    <TabsTrigger value="details" className="rounded-sm data-[state=active]:bg-white">
                      Change Log
                    </TabsTrigger>
                    <TabsTrigger value="queries" className="rounded-sm data-[state=active]:bg-white">
                      Queries ({selectedRevision.queries?.length || 0})
                    </TabsTrigger>
                    <TabsTrigger value="correspondence" className="rounded-sm data-[state=active]:bg-white">
                      Correspondence
                    </TabsTrigger>
                    <TabsTrigger value="links" className="rounded-sm data-[state=active]:bg-white">
                      Linked Items
                    </TabsTrigger>
                  </TabsList>

                  {/* Change Log Tab */}
                  <TabsContent value="details">
                    <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-5">
                      <h4 className="text-sm font-barlow font-bold uppercase text-slate-700 mb-3">
                        Revision Change Table
                      </h4>
                      {selectedRevision.change_log_entries?.length > 0 ? (
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-slate-200">
                              <th className="text-left p-2 text-xs uppercase text-slate-500">Rev</th>
                              <th className="text-left p-2 text-xs uppercase text-slate-500">Description</th>
                              <th className="text-left p-2 text-xs uppercase text-slate-500">Date</th>
                              <th className="text-left p-2 text-xs uppercase text-slate-500">By</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedRevision.change_log_entries.map((entry, idx) => (
                              <tr key={idx} className="border-b border-slate-100">
                                <td className="p-2 font-mono">{entry.rev_row_identifier || idx + 1}</td>
                                <td className="p-2 text-slate-700">{entry.change_description}</td>
                                <td className="p-2 text-slate-500">{entry.change_date || 'N/A'}</td>
                                <td className="p-2 text-slate-500">{entry.changed_by || 'N/A'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <p className="text-slate-400 text-sm">No change log entries extracted</p>
                      )}

                      {/* Diff Summary */}
                      {selectedRevision.diff_summary && (
                        <div className="mt-4 pt-4 border-t border-slate-100">
                          <h4 className="text-sm font-barlow font-bold uppercase text-slate-700 mb-2">
                            AI Diff Summary
                          </h4>
                          <p className="text-slate-600 text-sm">{selectedRevision.diff_summary.summary_text}</p>
                          {selectedRevision.diff_summary.detected_change_areas?.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs text-slate-500 uppercase font-bold mb-1">Detected Changes</p>
                              <ul className="list-disc list-inside text-sm text-slate-600">
                                {selectedRevision.diff_summary.detected_change_areas.map((area, i) => (
                                  <li key={i}>{area}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </TabsContent>

                  {/* Queries Tab */}
                  <TabsContent value="queries">
                    <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-5">
                      {selectedRevision.queries?.length > 0 ? (
                        <div className="space-y-4">
                          {selectedRevision.queries.map((query) => (
                            <div key={query.id} className="border border-slate-200 rounded-sm p-3">
                              <div className="flex justify-between items-start mb-2">
                                <Badge className={`${
                                  query.query_type === 'Internal' 
                                    ? 'bg-slate-100 text-slate-600' 
                                    : 'bg-blue-100 text-blue-700'
                                } rounded-sm text-xs`}>
                                  {query.query_type}
                                </Badge>
                                {query.is_resolved && (
                                  <Badge className="bg-emerald-100 text-emerald-700 rounded-sm text-xs">
                                    Resolved
                                  </Badge>
                                )}
                              </div>
                              <p className="text-slate-700 text-sm">{query.question_text}</p>
                              <p className="text-xs text-slate-400 mt-1">
                                Asked by {query.asked_by_name} on {formatDate(query.asked_at)}
                              </p>
                              {query.response_text && (
                                <div className="mt-3 pt-3 border-t border-slate-100">
                                  <p className="text-slate-600 text-sm">{query.response_text}</p>
                                  <p className="text-xs text-slate-400 mt-1">
                                    Responded by {query.responded_by_name} on {formatDate(query.responded_at)}
                                  </p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-400 text-sm">No queries yet</p>
                      )}
                    </div>
                  </TabsContent>

                  {/* Correspondence Tab */}
                  <TabsContent value="correspondence">
                    <div className="space-y-6">
                      {/* New Email Import System */}
                      <CorrespondenceTab 
                        projectId={projectId}
                        moduleType="Drawing"
                        moduleEntityId={drawingId}
                        title="Email Correspondence"
                      />
                      
                      {/* Legacy Correspondence */}
                      <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-5 mt-6">
                        <h4 className="text-sm font-bold uppercase text-slate-500 mb-4">Legacy Correspondence Log</h4>
                        {selectedRevision.correspondence_log?.length > 0 ? (
                          <div className="space-y-3">
                            {selectedRevision.correspondence_log.map((entry) => (
                              <div key={entry.id} className="border-l-2 border-slate-200 pl-3 py-2">
                                <div className="flex items-center gap-2">
                                  <Badge className={`${
                                    entry.direction === 'Incoming' ? 'bg-blue-100 text-blue-700' :
                                    entry.direction === 'Internal' ? 'bg-slate-100 text-slate-600' :
                                    'bg-emerald-100 text-emerald-700'
                                  } rounded-sm text-xs`}>
                                    {entry.direction}
                                  </Badge>
                                  <span className="text-slate-700 font-medium text-sm">{entry.subject}</span>
                                </div>
                                <p className="text-xs text-slate-400 mt-1">
                                  {formatDate(entry.timestamp)} - {entry.from_name}
                                </p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-slate-400 text-sm">No correspondence logged</p>
                        )}
                      </div>
                    </div>
                  </TabsContent>

                  {/* Linked Items Tab */}
                  <TabsContent value="links">
                    <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-5">
                      <div className="space-y-4">
                        {/* Linked RFIs */}
                        <div>
                          <h4 className="text-xs font-bold uppercase text-slate-500 mb-2">Linked RFIs</h4>
                          {selectedRevision.linked_rfis_details?.length > 0 ? (
                            <div className="space-y-2">
                              {selectedRevision.linked_rfis_details.map(rfi => (
                                <div 
                                  key={rfi.id}
                                  className="flex items-center justify-between p-2 border border-slate-200 rounded-sm cursor-pointer hover:bg-slate-50"
                                  onClick={() => navigate(`/projects/${projectId}/rfi/${rfi.id}`)}
                                >
                                  <div className="flex items-center gap-2">
                                    <FileText className="h-4 w-4 text-cyan-500" />
                                    <span className="font-mono text-sm">{rfi.rfi_number}</span>
                                    <span className="text-slate-600 text-sm">{rfi.title}</span>
                                  </div>
                                  <Badge className="rounded-sm text-xs">{rfi.status}</Badge>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-slate-400 text-sm">No linked RFIs</p>
                          )}
                        </div>

                        {/* Linked Delay Events */}
                        <div>
                          <h4 className="text-xs font-bold uppercase text-slate-500 mb-2">Linked Delay Events</h4>
                          {selectedRevision.linked_delay_events_details?.length > 0 ? (
                            <div className="space-y-2">
                              {selectedRevision.linked_delay_events_details.map(event => (
                                <div 
                                  key={event.id}
                                  className="flex items-center justify-between p-2 border border-orange-200 rounded-sm cursor-pointer hover:bg-orange-50"
                                  onClick={() => navigate(`/events/${event.id}`)}
                                >
                                  <div className="flex items-center gap-2">
                                    <AlertTriangle className="h-4 w-4 text-orange-500" />
                                    <span className="text-slate-700 text-sm">{event.event_type}</span>
                                  </div>
                                  <Badge className="rounded-sm text-xs">{event.status}</Badge>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-slate-400 text-sm">No linked delay events</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </>
            )}
          </div>
        </div>

        {/* Add Revision Dialog */}
        <Dialog open={showAddRevision} onOpenChange={setShowAddRevision}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-barlow uppercase flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Upload Revision
              </DialogTitle>
              <DialogDescription>
                Upload a new revision PDF. AI will analyze changes compared to the previous revision.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {/* PDF Upload */}
              <div className="border-2 border-dashed border-slate-200 rounded-sm p-4 text-center">
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleRevisionFileChange}
                  className="hidden"
                  id="revision-upload"
                />
                <label htmlFor="revision-upload" className="cursor-pointer">
                  {extractingMetadata ? (
                    <div className="flex flex-col items-center">
                      <Sparkles className="h-8 w-8 text-purple-500 animate-pulse mb-2" />
                      <p className="text-sm text-slate-600">Extracting metadata with AI...</p>
                    </div>
                  ) : revisionFile ? (
                    <div className="flex flex-col items-center">
                      <FileText className="h-8 w-8 text-emerald-500 mb-2" />
                      <p className="text-sm font-medium text-slate-700">{revisionFile.name}</p>
                      <p className="text-xs text-slate-500 mt-1">Click to replace</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <Upload className="h-8 w-8 text-slate-400 mb-2" />
                      <p className="text-sm text-slate-600">Click to upload revision PDF *</p>
                      <p className="text-xs text-slate-400 mt-1">AI will extract metadata and compare with previous revision</p>
                    </div>
                  )}
                </label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase">Revision Code *</Label>
                  <Input
                    value={revisionForm.revision_code}
                    onChange={(e) => setRevisionForm(prev => ({ ...prev, revision_code: e.target.value }))}
                    placeholder="e.g., 1, B, P02"
                    className="rounded-sm mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase">Issue Purpose</Label>
                  <Select 
                    value={revisionForm.issue_purpose}
                    onValueChange={(v) => setRevisionForm(prev => ({ ...prev, issue_purpose: v }))}
                  >
                    <SelectTrigger className="rounded-sm mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Information">Information</SelectItem>
                      <SelectItem value="Construction">Construction</SelectItem>
                      <SelectItem value="IFC">IFC</SelectItem>
                      <SelectItem value="Approval">Approval</SelectItem>
                      <SelectItem value="As-Built">As-Built</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-xs font-bold uppercase">Issued By Consultant *</Label>
                <Select 
                  value={revisionForm.issued_by_consultant_id}
                  onValueChange={(v) => {
                    // Find the selected consultant and auto-populate person name
                    const selectedConsultant = consultants.find(c => c.id === v);
                    setRevisionForm(prev => ({ 
                      ...prev, 
                      issued_by_consultant_id: v,
                      // Auto-populate person name from consultant's contact
                      issued_by_person: selectedConsultant?.contact_name || 
                        (selectedConsultant?.primary_contact?.name && selectedConsultant?.primary_contact?.surname 
                          ? `${selectedConsultant.primary_contact.name} ${selectedConsultant.primary_contact.surname}`
                          : prev.issued_by_person)
                    }));
                  }}
                >
                  <SelectTrigger className="rounded-sm mt-1">
                    <SelectValue placeholder="Select consultant" />
                  </SelectTrigger>
                  <SelectContent>
                    {consultants.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{c.company_name}</span>
                          {c.discipline && (
                            <span className="text-xs text-slate-500">({c.discipline})</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase">Issue Date</Label>
                  <Input
                    type="date"
                    value={revisionForm.issue_date}
                    onChange={(e) => setRevisionForm(prev => ({ ...prev, issue_date: e.target.value }))}
                    className="rounded-sm mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase">Received Date</Label>
                  <Input
                    type="date"
                    value={revisionForm.received_date}
                    onChange={(e) => setRevisionForm(prev => ({ ...prev, received_date: e.target.value }))}
                    className="rounded-sm mt-1"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs font-bold uppercase">Revision Notes *</Label>
                <Textarea
                  value={revisionForm.revision_notes}
                  onChange={(e) => setRevisionForm(prev => ({ ...prev, revision_notes: e.target.value }))}
                  placeholder="Describe the changes made in this revision..."
                  rows={3}
                  className="rounded-sm mt-1"
                />
                <p className="text-xs text-slate-400 mt-1">Brief description of what changed</p>
              </div>

              {/* AI Comparison Toggle */}
              <div className="flex items-center gap-2 p-3 bg-purple-50 border border-purple-200 rounded-sm">
                <Checkbox
                  id="ai-comparison"
                  checked={revisionForm.run_ai_comparison}
                  onCheckedChange={(checked) => setRevisionForm(prev => ({ ...prev, run_ai_comparison: checked }))}
                />
                <label htmlFor="ai-comparison" className="text-sm text-purple-700 cursor-pointer">
                  <span className="font-medium">Run AI comparison</span>
                  <span className="text-purple-600 block text-xs">Automatically detect clouded areas and analyze changes vs previous revision</span>
                </label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddRevision(false)} className="rounded-sm">Cancel</Button>
              <Button 
                onClick={handleAddRevision} 
                disabled={uploadingRevision || !revisionForm.pdf_file_url}
                className="rounded-sm bg-slate-900"
              >
                {uploadingRevision ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                    {revisionForm.run_ai_comparison ? 'Uploading & Analyzing...' : 'Uploading...'}
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-1" />
                    Upload Revision
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create RFI Dialog */}
        <Dialog open={showCreateRfi} onOpenChange={setShowCreateRfi}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-barlow uppercase">Create RFI from Drawing</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-bold uppercase">RFI Title *</Label>
                <Input
                  value={rfiForm.title}
                  onChange={(e) => setRfiForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Query title"
                  className="rounded-sm mt-1"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase">Query *</Label>
                <Textarea
                  value={rfiForm.query}
                  onChange={(e) => setRfiForm(prev => ({ ...prev, query: e.target.value }))}
                  placeholder="Your question or clarification request..."
                  rows={4}
                  className="rounded-sm mt-1"
                />
                <p className="text-xs text-slate-400 mt-1">Drawing context will be automatically added</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase">Priority</Label>
                  <Select 
                    value={rfiForm.priority}
                    onValueChange={(v) => setRfiForm(prev => ({ ...prev, priority: v }))}
                  >
                    <SelectTrigger className="rounded-sm mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Low">Low</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="High">High</SelectItem>
                      <SelectItem value="Critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase">Response Due</Label>
                  <Input
                    type="date"
                    value={rfiForm.required_response_date}
                    onChange={(e) => setRfiForm(prev => ({ ...prev, required_response_date: e.target.value }))}
                    className="rounded-sm mt-1"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateRfi(false)} className="rounded-sm">Cancel</Button>
              <Button onClick={handleCreateRfi} className="rounded-sm bg-cyan-600 text-white">Create RFI</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Convert to Delay Event Dialog */}
        <Dialog open={showConvertEvent} onOpenChange={setShowConvertEvent}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-barlow uppercase">Convert to Delay Event</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-bold uppercase">Event Type *</Label>
                <Input
                  value={eventForm.event_type}
                  onChange={(e) => setEventForm(prev => ({ ...prev, event_type: e.target.value }))}
                  placeholder="e.g., Late Drawing Issue"
                  className="rounded-sm mt-1"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase">Responsible Party</Label>
                <Select 
                  value={eventForm.responsible_party}
                  onValueChange={(v) => setEventForm(prev => ({ ...prev, responsible_party: v }))}
                >
                  <SelectTrigger className="rounded-sm mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Consultant">Consultant</SelectItem>
                    <SelectItem value="Employer">Employer</SelectItem>
                    <SelectItem value="Contractor">Contractor</SelectItem>
                    <SelectItem value="Subcontractor">Subcontractor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase">Start Date</Label>
                <Input
                  type="date"
                  value={eventForm.start_date}
                  onChange={(e) => setEventForm(prev => ({ ...prev, start_date: e.target.value }))}
                  className="rounded-sm mt-1"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase">Additional Narrative</Label>
                <Textarea
                  value={eventForm.narrative}
                  onChange={(e) => setEventForm(prev => ({ ...prev, narrative: e.target.value }))}
                  placeholder="Additional context..."
                  rows={3}
                  className="rounded-sm mt-1"
                />
                <p className="text-xs text-slate-400 mt-1">Drawing details will be automatically included</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConvertEvent(false)} className="rounded-sm">Cancel</Button>
              <Button onClick={handleConvertToEvent} className="rounded-sm bg-orange-600 text-white">Create Event</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Distribute Dialog */}
        <Dialog open={showDistribute} onOpenChange={setShowDistribute}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-barlow uppercase">Distribute Drawing</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-slate-600">Select team members to distribute to:</p>
              {internalTeam.length > 0 ? (
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {internalTeam.map(member => (
                    <label key={member.id} className="flex items-center gap-2 p-2 border border-slate-200 rounded-sm cursor-pointer hover:bg-slate-50">
                      <Checkbox
                        checked={selectedRecipients.some(r => r.user_id === member.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedRecipients(prev => [...prev, {
                              user_id: member.id,
                              name: member.name,
                              email: member.email
                            }]);
                          } else {
                            setSelectedRecipients(prev => prev.filter(r => r.user_id !== member.id));
                          }
                        }}
                      />
                      <div>
                        <p className="text-sm font-medium">{member.name}</p>
                        <p className="text-xs text-slate-500">{member.role}</p>
                      </div>
                    </label>
                  ))}
                </div>
              ) : (
                <p className="text-slate-400 text-sm">No internal team members configured</p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDistribute(false)} className="rounded-sm">Cancel</Button>
              <Button 
                onClick={handleDistribute} 
                disabled={selectedRecipients.length === 0}
                className="rounded-sm bg-slate-900"
              >
                Distribute ({selectedRecipients.length})
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Query Dialog */}
        <Dialog open={showAddQuery} onOpenChange={setShowAddQuery}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-barlow uppercase">Add Query</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-bold uppercase">Query Type</Label>
                <Select 
                  value={queryForm.query_type}
                  onValueChange={(v) => setQueryForm(prev => ({ ...prev, query_type: v }))}
                >
                  <SelectTrigger className="rounded-sm mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Internal">Internal</SelectItem>
                    <SelectItem value="External">External (Consultant)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase">Question *</Label>
                <Textarea
                  value={queryForm.question_text}
                  onChange={(e) => setQueryForm(prev => ({ ...prev, question_text: e.target.value }))}
                  placeholder="Your question..."
                  rows={4}
                  className="rounded-sm mt-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddQuery(false)} className="rounded-sm">Cancel</Button>
              <Button onClick={handleAddQuery} className="rounded-sm bg-slate-900">Add Query</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
